import { Card, Group, Skeleton, Stack, Text, ThemeIcon, UnstyledButton } from "@mantine/core";
import { IconAlertCircle, IconAlertTriangle, IconChevronRight, IconCircleCheck, IconInfoCircle } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import type { AlertSeverity, DashboardData } from "../../../apis/myDashboard";
import { SectionHeader } from "../SectionHeader";

interface Props {
  data?: DashboardData;
  loading: boolean;
}

const SEVERITY: Record<AlertSeverity, { color: string; Icon: typeof IconInfoCircle }> = {
  critical: { color: "red", Icon: IconAlertTriangle },
  warning : { color: "yellow", Icon: IconAlertCircle },
  info    : { color: "grape", Icon: IconInfoCircle }
};

// ROW 4 — rule-based action items, already sorted critical → info server-side
// (UI-SPEC §6.1). Each row is keyboard-focusable and deep-links to the fix.
export function AlertsPanel({ data, loading }: Props) {
  const navigate = useNavigate();
  const alerts = data?.alerts ?? [];

  return (
    <Card withBorder radius="md" padding="md" h="100%">
      <Stack gap="sm">
        <SectionHeader title="Анхаарах" eyebrow="Үйлдэл шаардсан" />
        {loading ? (
          <Stack gap="sm">{[0, 1].map((i) => <Skeleton key={i} height={44} radius="sm" />)}</Stack>
        ) : alerts.length === 0 ? (
          <Group gap="sm" py="lg" justify="center">
            <ThemeIcon variant="light" color="green" size={36} radius="xl"><IconCircleCheck size={22} /></ThemeIcon>
            <Text c="dimmed">Бүх зүйл хэвийн.</Text>
          </Group>
        ) : (
          <Stack gap="xs">
            {alerts.map((a, i) => {
              const s = SEVERITY[a.severity] ?? SEVERITY.info;
              return (
                <UnstyledButton key={`${a.code}-${i}`} onClick={() => navigate(a.link)} w="100%">
                  <Group gap="sm" wrap="nowrap" p="xs">
                    <ThemeIcon variant="light" color={s.color} size={32} radius="md"><s.Icon size={18} /></ThemeIcon>
                    <Text size="sm" flex={1}>{a.message}</Text>
                    <IconChevronRight size={16} color="var(--mantine-color-gray-5)" />
                  </Group>
                </UnstyledButton>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
